/**
 * Accordion controller to toggle active state on wrappers and triggers.
 * Uses event delegation for better performance and dynamic content support.
 */
class Accordion {
    /**
     * @param {Object} options - Accordion configuration.
     * @param {string} options.wrapperAttr - Data attribute value for wrapper selector.
     * @param {string} options.triggerAttr - Data attribute value for trigger selector.
     * @param {string} options.activeClass - Class name toggled on wrapper and triggers.
     */
    constructor({
        wrapperAttr = 'wrapper',
        triggerAttr = 'trigger',
        activeClass = 'is-active'
    } = {}) {
        this.wrapperAttr = wrapperAttr;
        this.triggerAttr = triggerAttr;
        this.activeClass = activeClass;

        /** @type {Map<HTMLElement, Function>} */
        this.listeners = new Map();
    }

    /**
     * Attach delegated click listeners to all accordion wrappers.
     */
    init() {
        const wrappers = document.querySelectorAll(`[data-accordion="${this.wrapperAttr}"]`);

        if (!wrappers.length) {
            return;
        }

        wrappers.forEach((wrapper) => {
            const handler = (event) => {
                const trigger = event.target.closest(`[data-accordion="${this.triggerAttr}"]`);

                if (!trigger || !wrapper.contains(trigger)) {
                    return;
                }

                this.toggle(wrapper);
            };

            wrapper.addEventListener('click', handler);
            this.listeners.set(wrapper, handler);
        });
    }

    /**
     * Remove all attached event listeners.
     */
    destroy() {
        this.listeners.forEach((handler, wrapper) => {
            wrapper.removeEventListener('click', handler);
        });

        this.listeners.clear();
    }

    /**
     * Toggle active state for wrapper and its triggers.
     *
     * @param {HTMLElement} wrapper - Accordion wrapper element.
     */
    toggle(wrapper) {
        const triggers = wrapper.querySelectorAll(`[data-accordion="${this.triggerAttr}"]`);
        const isActive = wrapper.classList.toggle(this.activeClass);

        triggers.forEach((trigger) => {
            trigger.classList.toggle(this.activeClass, isActive);
            trigger.setAttribute('aria-expanded', String(isActive));
        });

        wrapper.dispatchEvent(new CustomEvent(isActive ? 'accordion:open' : 'accordion:close', {
            bubbles : true,
            detail  : { wrapper }
        }));
    }
}

export { Accordion };
